import { useCallback, useState } from 'react';

import {
  useFragment as useApolloFragment,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { Box, Skeleton, Stack } from '@mui/material';

import {
  type Address_Display_Options_Enum_Enum,
  Doc_Template_Type_Enum_Enum,
  type StepCoverPhotoBrochureFragment,
} from '@graphql/__generated__/graphql';
import { DOC_TEMPLATE_THEMES } from '@modules/_shared/common/queries';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import {
  DOC_TEMPLATES_BROCHURE_REPORT,
  STEP_COVER_PHOTO_BROCHURE_FRAGMENT,
  UPDATE_TEAM_BRANDING_COLOR_BROCHURE,
} from '@modules/reports/brochure-report/brochureReportsQueries';
import CoverPhotoSelector from '@modules/reports/shared/CoverPhotoSelector/CoverPhotoSelector';
import { DescriptionLanguageTabs } from '@modules/reports/shared/DescriptionLanguageTabs';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import {
  getBrokerLanguage,
  useLanguageTranslations,
  useTranslationUpdate,
} from '@modules/reports/shared/language';
import { ReportSettingsSection } from '@modules/reports/shared/ReportSettingsSection';
import { useRefetchWhenMissing } from '@modules/reports/shared/useRefetchWhenMissing';
import {
  getFilteredPropertyImages,
  useUpdateListingBrochure,
} from '@modules/reports/shared/utils';
import { useDebouncedHandler } from '@shared/hooks/debounce';

type StepCoverForm = {
  use_custom_brochure_title: boolean;
  hide_price_on_brochure: boolean;
  address_display_option_brochure: string;
  brochure_show_agent_on_brochure: boolean;
  font_family: string | null;
  doc_template_id: string;
  doc_template_theme_id: string | null;
  branding_color: string | null;
};

const StepCoverPhotoBrochure = (props: ReportComponentProps) => {
  const { reportId: listingId } = props;
  const { descriptionLanguage, setDescriptionLanguage, setError } =
    useReportContext();

  const { complete, data } = useApolloFragment<StepCoverPhotoBrochureFragment>({
    fragment: STEP_COVER_PHOTO_BROCHURE_FRAGMENT,
    fragmentName: 'StepCoverPhotoBrochure',
    from: {
      __typename: 'lots',
      id: listingId,
    },
  });

  const { data: docTemplatesData, refetch: refetchDocTemplates } = useQuery(
    DOC_TEMPLATES_BROCHURE_REPORT,
  );
  const currentTemplateId = data?.doc_template?.id;
  useRefetchWhenMissing(
    currentTemplateId,
    docTemplatesData?.doc_templates,
    refetchDocTemplates,
    setError,
  );
  const { data: themeTemplatesData, refetch: refetchThemeTemplates } = useQuery(
    DOC_TEMPLATE_THEMES,
    {
      variables: { type: Doc_Template_Type_Enum_Enum.LotBrochureTheme },
    },
  );
  useRefetchWhenMissing(
    data?.doc_template_theme_id,
    themeTemplatesData?.doc_templates,
    refetchThemeTemplates,
    setError,
  );

  const [updateListingBrochure, updating] = useUpdateListingBrochure(
    listingId,
    'page-cover',
  );
  const [updateTeamBrandingColor] = useMutation(
    UPDATE_TEAM_BRANDING_COLOR_BROCHURE,
  );

  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const brokerLang = getBrokerLanguage(data?.broker);
  const titleTranslations = useLanguageTranslations(data, 'brochure_title', {
    fallbackValue: data?.title ?? null,
    fallbackLanguage: brokerLang,
  });

  const {
    updateTranslation: updateTitleTranslation,
    debouncedTranslationUpdate: debouncedTitleTranslationUpdate,
  } = useTranslationUpdate(updateListingBrochure, 'brochure_title');

  const update = useCallback(
    async (formData: Partial<StepCoverForm>) => {
      const mutation: Record<string, unknown> = {
        use_custom_brochure_title: formData.use_custom_brochure_title,
        hide_price_on_brochure: formData.hide_price_on_brochure,
        address_display_option_brochure:
          formData.address_display_option_brochure as Address_Display_Options_Enum_Enum,
        brochure_show_agent_on_brochure:
          formData.brochure_show_agent_on_brochure,
        brochure_font_family: formData.font_family,
        brochure_doc_template_id: formData.doc_template_id || undefined,
        doc_template_theme_id: formData.doc_template_theme_id,
      };

      // Seed brochure title from listing title when toggling, only if empty
      const brochureTitleKey = `brochure_title_${brokerLang}`;
      const currentBrochureTitle =
        data?.[brochureTitleKey as keyof typeof data];

      if (isNullOrEmpty(currentBrochureTitle, true)) {
        mutation[brochureTitleKey] = data?.title ?? null;
      }

      await updateListingBrochure(mutation);
    },
    [brokerLang, data, updateListingBrochure],
  );

  const debouncedUpdate = useDebouncedHandler(300, update);

  const onChangeHandler: RaFormOnChange<StepCoverForm> = useCallback(
    (formData, name, type) => {
      if (type !== 'change') {
        return;
      }

      if (name === 'branding_color') {
        const teamId = data?.broker?.default_team?.id;
        if (teamId) {
          updateTeamBrandingColor({
            variables: {
              teamId,
              brandingColor: formData.branding_color,
              reportId: listingId,
            },
          });
        }
        return;
      }

      debouncedUpdate(formData);
    },
    [
      debouncedUpdate,
      data?.broker?.default_team?.id,
      updateTeamBrandingColor,
      listingId,
    ],
  );

  const handleUpdateImage = useCallback(
    (newCoverImageId: string | null) => {
      updateListingBrochure({
        brochure_cover_image_id: newCoverImageId,
      });
    },
    [updateListingBrochure],
  );

  const brochureStepCoverFormDefinition: FormDefinitionType<StepCoverForm> =
    useCallback(
      ({ t }) => [
        {
          type: 'custom',
          name: 'settings_section',
          element: (
            <ReportSettingsSection
              expanded={settingsExpanded}
              onToggle={() => setSettingsExpanded(!settingsExpanded)}
              docTemplatesData={docTemplatesData?.doc_templates}
              themeTemplatesData={themeTemplatesData?.doc_templates}
              currentTemplateId={currentTemplateId}
              currentThemeId={data?.doc_template_theme_id}
              currentThemeName={data?.doc_template_theme?.name}
              includeLanguage={false}
            />
          ),
          gridProps: { md: 12 },
        },
        {
          name: 'use_custom_brochure_title',
          label: t('Use custom listing title'),
          type: 'checkbox',
          gridProps: { md: 12 },
          style: 'switch',
        },
        {
          name: 'title_language_tabs',
          type: 'custom',
          gridProps: { md: 12 },
          element: (
            <DescriptionLanguageTabs
              variant="plain-text"
              defaultLanguage={descriptionLanguage}
              translations={titleTranslations}
              onTranslationChange={debouncedTitleTranslationUpdate}
              onTranslationComplete={updateTitleTranslation}
              onTranslationDelete={lang =>
                updateListingBrochure({ [`brochure_title_${lang}`]: null })
              }
              onDescriptionLanguageChange={setDescriptionLanguage}
              disabled={!data?.use_custom_brochure_title}
            />
          ),
        },
        {
          name: 'hide_price_on_brochure',
          label: t('Hide price on brochure'),
          type: 'checkbox',
          gridProps: { md: 12 },
          style: 'switch',
        },
        {
          name: 'address_display_option_brochure',
          label: t('Address'),
          type: 'select',
          gridProps: { md: 12 },
          options: () => [
            { label: t('Full address'), value: 'full_address' },
            {
              label: t('Hide street number only'),
              value: 'hide_street_number',
            },
            { label: t('Hide street and street number'), value: 'hide_street' },
          ],
        },
        {
          name: 'brochure_show_agent_on_brochure',
          label: t('Show agent on brochure'),
          type: 'checkbox',
          gridProps: { md: 12 },
          style: 'switch',
        },
        {
          type: 'custom',
          name: 'cover',
          element: (
            <CoverPhotoSelector
              propertyImages={getFilteredPropertyImages(
                data?.property?.property_images,
              )}
              coverImageId={data?.brochure_cover_image?.id ?? ''}
              propertyId={data?.property?.id ?? ''}
              type="lots"
              onUpdate={handleUpdateImage}
              fragment={STEP_COVER_PHOTO_BROCHURE_FRAGMENT}
              fragmentName="StepCoverPhotoBrochure"
            />
          ),
          gridProps: { md: 12 },
        },
      ],
      [
        currentTemplateId,
        data?.brochure_cover_image?.id,
        data?.doc_template_theme_id,
        data?.doc_template_theme?.name,
        data?.property?.id,
        data?.property?.property_images,
        data?.use_custom_brochure_title,
        debouncedTitleTranslationUpdate,
        descriptionLanguage,
        docTemplatesData?.doc_templates,
        handleUpdateImage,
        setDescriptionLanguage,
        settingsExpanded,
        themeTemplatesData?.doc_templates,
        titleTranslations,
        updateListingBrochure,
        updateTitleTranslation,
      ],
    );

  const onSubmit = useCallback(
    async (formData: StepCoverForm) => {
      await update(formData);
      props.setStep(props.step + 1);
    },
    [update, props],
  );

  if (!complete) {
    return (
      <Box sx={{ flexGrow: 1, position: 'relative', overflowY: 'auto', p: 2 }}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Stack
              key={`step-cover-skeleton-${index}`}
              sx={{
                gap: 1,
              }}
            >
              <Skeleton variant="rounded" height={20} width="30%" />
              <Skeleton variant="rounded" height={40} />
            </Stack>
          ))}
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <RaForm
        freezeInitialDefaultValues={true}
        formDefinition={brochureStepCoverFormDefinition}
        defaultValues={{
          use_custom_brochure_title: data?.use_custom_brochure_title,
          hide_price_on_brochure: data?.hide_price_on_brochure,
          address_display_option_brochure:
            data?.address_display_option_brochure,
          brochure_show_agent_on_brochure:
            data?.brochure_show_agent_on_brochure,
          font_family: data?.brochure_font_family || 'default',
          doc_template_id: data?.doc_template?.id || '',
          doc_template_theme_id: data?.doc_template_theme_id ?? null,
          branding_color: data?.broker?.default_team?.branding_color || null,
        }}
        onSubmit={onSubmit}
        onChange={onChangeHandler}
        contentScrollable
        actionButtonsComponent={
          <FooterActions {...props} hideBackButton updating={updating} />
        }
      />
    </>
  );
};

export default StepCoverPhotoBrochure;
